import React, { useState } from 'react';
import { Button, TextButton, Text } from '@amzn/storm-ui';
import { useFormContext } from 'react-hook-form';
import useIntl from '@i18n/useIntl';
import * as formConstants from '@components/form/constants';
import ConfirmationModal from '@components/modals/ConfirmationModal';
import { toggleAccountInfoEditMode } from 'src/shared/state/account-settings-slice';
import useSubmit from 'src/pages/accountSettings/hooks/useSubmit';
import {
  ACCOUNT_SETTINGS_CONFIRM_MODAL_ID,
  CONFIRMATION_MODAL_CANCEL_BUTTON_TRANSLATION_KEY,
  CONFIRMATION_MODAL_CONFIRM_BUTTON_TRANSLATION_KEY,
  CONFIRMATION_MODAL_HEADER_TRANSLATION_KEY,
  CONFIRMATION_MODAL_MESSAGE_TRANSLATION_KEY,
} from 'src/pages/accountSettings/constants';
import { ActionsButtonsWrapper } from '@components/form/style';
import {
  AccountSettingsControllersWrapper,
  AccountSettingsForm,
} from 'src/pages/accountSettings/style';
import AccountAttributesControllers from '@features/account-settings/components/AccountAttributesControllers';
import BusinessDetailsControllers from '@features/account-settings/components/BusinessDetailsControllers';
import { ControllerConfigurations } from '@features/account-settings/types';
import * as accountInfoConstants from '@features/account-settings/constants';

interface AccountDetailsFormProps {
  configuration?: ControllerConfigurations;
}

const AccountDetailsForm = ({ configuration }: AccountDetailsFormProps) => {
  const { intl } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { handleSubmit, getValues } = useFormContext();
  const { isLoading, onSubmit, onReset } = useSubmit(toggleAccountInfoEditMode);

  const cancelChangesHandler = () => {
    setIsModalOpen(false);
    onReset();
  };

  const currency = getValues(
    accountInfoConstants.ACCOUNT_CURRENCY_FIELD_CONTROLLER_NAME,
  );
  const timezone = getValues(
    accountInfoConstants.ACCOUNT_TIME_ZONE_FIELD_CONTROLLER_NAME,
  );
  const industry = getValues(
    accountInfoConstants.ACCOUNT_INDUSTRY_FIELD_CONTROLLER_NAME,
  );

  return (
    <AccountSettingsForm onSubmit={handleSubmit(onSubmit)}>
      <AccountSettingsControllersWrapper>
        <BusinessDetailsControllers configuration={configuration} />
        {currency && timezone && industry && (
          <AccountAttributesControllers configuration={configuration} />
        )}
      </AccountSettingsControllersWrapper>
      <ActionsButtonsWrapper>
        <Button variant="primary" type="submit" loading={isLoading}>
          {intl.getMessage(formConstants.SUBMIT_BUTTON_TRANSLATION_KEY)}
        </Button>
        <TextButton onClick={() => setIsModalOpen(true)}>
          {intl.getMessage(formConstants.CANCEL_BUTTON_TRANSLATION_KEY)}
        </TextButton>
      </ActionsButtonsWrapper>
      <ConfirmationModal
        id={ACCOUNT_SETTINGS_CONFIRM_MODAL_ID}
        header={
          <Text size={'large'}>
            {intl.getMessage(CONFIRMATION_MODAL_HEADER_TRANSLATION_KEY)}
          </Text>
        }
        isOpen={isModalOpen}
        cancelButtonText={intl.getMessage(
          CONFIRMATION_MODAL_CANCEL_BUTTON_TRANSLATION_KEY,
        )}
        confirmButtonText={intl.getMessage(
          CONFIRMATION_MODAL_CONFIRM_BUTTON_TRANSLATION_KEY,
        )}
        onConfirm={cancelChangesHandler}
        onCancel={() => setIsModalOpen(false)}
      >
        <Text size={'base'}>
          {intl.getMessage(CONFIRMATION_MODAL_MESSAGE_TRANSLATION_KEY)}
        </Text>
      </ConfirmationModal>
    </AccountSettingsForm>
  );
};

export default AccountDetailsForm;
